import React, { useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@/utils/cn';

export const MAX_PREVIEW_CHARS = 240;
export const MAX_PREVIEW_LINES = 5;
export const PREVIEW_MAX_HEIGHT_PX = 80;
export const EXPANDED_BOTTOM_PADDING_PX = 12;

// Convert TipTap-style task list HTML into markdown checkboxes so we can reuse markdown processing
function convertTiptapTaskHtml(input: string): string {
  const normalized = input.replace(/\u00a0/g, ' ');
  return normalized
    .split('\n')
    .map((line) => {
      const match = line.match(/<label[^>]*>[\s\S]*?<input[^>]*type=["']checkbox["'][^>]*>[\s\S]*?<\/label>\s*<div[^>]*>([\s\S]*?)<\/div>/i);
      if (!match) return line;
      const content = match[1] ?? '';
      const isChecked = /<input[^>]*type=["']checkbox["'][^>]*checked/i.test(line);
      const marker = isChecked ? '- [x] ' : '- [ ] ';
      return `${marker}${content.trim()}`;
    })
    .join('\n');
}

// Convert markdown tables to HTML tables
function convertMarkdownTables(markdown: string): string {
  const lines = markdown.split('\n');
  const output: string[] = [];

  let i = 0;
  while (i < lines.length) {
    const headerMatch = /^\s*\|(.+)\|\s*$/.exec(lines[i]);
    const separatorMatch = i + 1 < lines.length ? /^\s*\|(?:\s*:?-+:?\s*\|)+\s*$/.exec(lines[i + 1]) : null;

    if (headerMatch && separatorMatch) {
      const headerCells = headerMatch[1]
        .split('|')
        .map((cell) => cell.trim())
        .filter((cell) => cell.length > 0);

      const rows: string[][] = [];
      let rowIndex = i + 2;

      while (rowIndex < lines.length) {
        const rowMatch = /^\s*\|(.+)\|\s*$/.exec(lines[rowIndex]);
        if (!rowMatch) {
          break;
        }

        const rowCells = rowMatch[1]
          .split('|')
          .map((cell) => cell.trim());
        rows.push(rowCells);
        rowIndex += 1;
      }

      const tableHtml = [
        '<table class="note-table">',
        '<thead>',
        `<tr>${headerCells.map((cell) => `<th>${cell}</th>`).join('')}</tr>`,
        '</thead>',
        '<tbody>',
        rows
          .map((rowCells) => {
            const paddedRow = rowCells.length < headerCells.length
              ? [...rowCells, ...Array(headerCells.length - rowCells.length).fill('')]
              : rowCells;
            return `<tr>${paddedRow.map((cell) => `<td>${cell}</td>`).join('')}</tr>`;
          })
          .join(''),
        '</tbody>',
        '</table>',
      ].join('');

      output.push(tableHtml);
      i = rowIndex;
      continue;
    }

    output.push(lines[i]);
    i += 1;
  }

  return output.join('\n');
}

// Allow existing HTML tables by sanitizing and normalizing them
function allowExistingHtmlTables(html: string): string {
  return html.replace(/&lt;table[^&]*&gt;[\s\S]*?&lt;\/table&gt;/gi, (match) => {
    const unescaped = match
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&');

    const stripped = unescaped
      .replace(/<colgroup>[\s\S]*?<\/colgroup>/gi, '')
      .replace(/<table[^>]*>/i, '<table class="note-table">')
      .replace(/<th[^>]*>/gi, '<th>')
      .replace(/<td[^>]*>/gi, '<td>');

    return stripped;
  });
}

// Convert markdown to HTML for rendering
export function markdownToHtml(markdown: string, options: { escapeHtml?: boolean } = {}): string {
  const { escapeHtml = true } = options;

  // Normalize known TipTap task list HTML into markdown form
  let html = convertTiptapTaskHtml(markdown.replace(/\u00a0/g, ' '));

  // Escape HTML entities first (but preserve markdown syntax)
  if (escapeHtml) {
    html = html
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  // Handle alignment fences :::align-center|right|justify ... ::: after escaping so tags render
  html = html.replace(
    /:::align-(center|right|justify)\s*\n?([\s\S]*?)\n?:::/gi,
    (_match, align, content) => `<p data-text-align="${align}">${content}</p>`
  );

  // Normalize/allow existing HTML tables generated by editors (e.g., TipTap)
  html = allowExistingHtmlTables(html);

  // Tables
  html = convertMarkdownTables(html);

  // Headings (must be at start of line)
  html = html.replace(/^#### (.+)$/gm, '<h4>$1</h4>');
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>');

  // Bold: **text** or __text__
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

  // Italic: *text* or _text_ (but not inside words with underscores)
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  html = html.replace(/(?<!\w)_([^_]+)_(?!\w)/g, '<em>$1</em>');

  // Strikethrough: ~~text~~
  html = html.replace(/~~(.+?)~~/g, '<del>$1</del>');

  // Highlight: ==text==
  html = html.replace(/==(.+?)==/g, '<mark>$1</mark>');

  // Underline: ++text++
  html = html.replace(/\+\+(.+?)\+\+/g, '<u>$1</u>');

  // Links: [text](url)
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

  // Task lists: allow leading spaces (including nbsp) before "- [x]" / "- [ ]"
  html = html.replace(
    /^[\s\u00a0]*-\s*\[x\]\s+(.+)$/gim,
    '<li class="task-item checked"><input type="checkbox" checked disabled aria-label="task complete" /> <span class="task-text task-text-complete">$1</span></li>'
  );
  html = html.replace(
    /^[\s\u00a0]*-\s*\[\s\]\s+(.+)$/gim,
    '<li class="task-item"><input type="checkbox" disabled aria-label="task not complete" /> <span class="task-text">$1</span></li>'
  );

  // Unordered lists: - item or * item
  html = html.replace(/^[-*] (.+)$/gm, '<li>$1</li>');

  // Ordered lists: 1. item
  html = html.replace(/^\d+\. (.+)$/gm, '<li class="ordered">$1</li>');

  // Wrap consecutive list items
  html = html.replace(/((?:<li class="task-item[^"]*">.*?<\/li>\n?)+)/g, '<ul class="task-list">$1</ul>');
  html = html.replace(/((?:<li class="ordered">.*?<\/li>\n?)+)/g, '<ol>$1</ol>');
  html = html.replace(/((?:<li>(?!class)[^<]*<\/li>\n?)+)/g, '<ul>$1</ul>');

  // Newlines to <br /> for remaining content (but not after block elements)
  html = html.replace(/\n(?!<)/g, '<br />');

  // Clean up
  html = html.replace(/<br \/>(<\/(?:ul|ol|blockquote|h[1-4])>)/g, '$1');
  html = html.replace(/(<(?:ul|ol|blockquote|h[1-4])[^>]*>)<br \/>/g, '$1');

  return html;
}

// Get plain text from markdown for length calculations
function getPlainTextFromMarkdown(markdown: string): string {
  let text = markdown;
  // Remove markdown syntax
  text = text.replace(/^#{1,4}\s+/gm, ''); // headings
  text = text.replace(/\*\*(.+?)\*\*/g, '$1'); // bold
  text = text.replace(/__(.+?)__/g, '$1');
  text = text.replace(/\*([^*]+)\*/g, '$1'); // italic
  text = text.replace(/_([^_]+)_/g, '$1');
  text = text.replace(/~~(.+?)~~/g, '$1'); // strikethrough
  text = text.replace(/==(.+?)==/g, '$1'); // highlight
  text = text.replace(/\+\+(.+?)\+\+/g, '$1'); // underline
  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1'); // links
  text = text.replace(/^[-*] \[[ x]\] /gm, ''); // task list markers
  text = text.replace(/^[-*] /gm, ''); // list markers
  text = text.replace(/^\d+\. /gm, ''); // ordered list markers
  text = text.replace(/^> /gm, ''); // blockquote markers
  text = text.replace(/^\|(.+)\|\s*$/gm, (_match, row) =>
    row
      .split('|')
      .map((cell: string) => cell.trim())
      .join(' ')
  ); // table rows
  text = text.replace(/^\|\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|\s*$/gm, ''); // table separator rows
  text = text.replace(/\s{2,}/g, ' ');
  return text;
}

// Calculate effective line weight for a markdown line (headings count as more lines)
function getLineWeight(line: string): number {
  if (/^# /.test(line)) return 2.5;      // h1 - largest
  if (/^## /.test(line)) return 2;       // h2
  if (/^### /.test(line)) return 1.5;    // h3
  if (/^#### /.test(line)) return 1.25;  // h4
  return 1; // regular line
}

// Calculate total effective lines considering heading heights
function getEffectiveLineCount(lines: string[]): number {
  return lines.reduce((sum, line) => sum + getLineWeight(line), 0);
}

// Truncate markdown content
export function truncateMarkdown(markdown: string, maxChars: number, maxLines: number): { truncated: string; needsTruncation: boolean } {
  const plainText = getPlainTextFromMarkdown(markdown);
  const lines = markdown.split('\n');

  const exceedsChars = plainText.length > maxChars;
  const effectiveLines = getEffectiveLineCount(lines);
  const exceedsLines = effectiveLines > maxLines;
  const needsTruncation = exceedsChars || exceedsLines;

  if (!needsTruncation) {
    return { truncated: markdown, needsTruncation: false };
  }

  // Truncate by effective lines first, then by characters
  let truncatedLines: string[] = [];
  let currentWeight = 0;

  for (const line of lines) {
    const weight = getLineWeight(line);
    if (currentWeight + weight > maxLines) {
      break;
    }
    truncatedLines.push(line);
    currentWeight += weight;
  }

  // Ensure at least one line
  if (truncatedLines.length === 0 && lines.length > 0) {
    truncatedLines = [lines[0]];
  }

  let truncated = truncatedLines.join('\n');

  const truncatedPlainText = getPlainTextFromMarkdown(truncated);
  if (truncatedPlainText.length > maxChars) {
    // Character truncation - find a good break point
    let charCount = 0;
    let lineIndex = 0;
    for (let i = 0; i < truncatedLines.length; i++) {
      const lineText = getPlainTextFromMarkdown(truncatedLines[i]);
      if (charCount + lineText.length > maxChars) {
        lineIndex = i;
        break;
      }
      charCount += lineText.length + 1; // +1 for newline
      lineIndex = i + 1;
    }
    truncated = truncatedLines.slice(0, Math.max(1, lineIndex)).join('\n');
  }

  return { truncated: truncated + '…', needsTruncation: true };
}

// CSS styles for rendering markdown content
export const getNoteContentStyles = (isDark: boolean) => `
  .note-content ul {
    list-style-type: disc;
    padding-left: 1.5rem;
    margin: 0.25rem 0;
  }
  .note-content ol {
    list-style-type: decimal;
    padding-left: 1.5rem;
    margin: 0.25rem 0;
  }
  .note-content li {
    margin: 0.125rem 0;
  }
  .note-content li.ordered {
    list-style-type: decimal;
  }
  .note-content ul.task-list {
    list-style: none;
    padding-left: 0;
  }
  .note-content li.task-item {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
  }
  .note-content li.task-item input[type="checkbox"] {
    margin-top: 0.25rem;
    accent-color: ${isDark ? '#60a5fa' : '#2563eb'};
  }
  .note-content li.task-item.checked {
    color: ${isDark ? '#6b7280' : '#9ca3af'};
  }
  .note-content li.task-item .task-text {
    flex: 1;
  }
  .note-content li.task-item .task-text-complete {
    text-decoration: line-through;
    color: ${isDark ? '#9ca3af' : '#6b7280'};
  }
  .note-content table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.5rem 0;
    min-width: 0 !important;
  }
  .note-content table col {
    min-width: 0 !important;
    width: auto !important;
  }
  .note-content table th,
  .note-content table td {
    border: 1px solid ${isDark ? '#374151' : '#e5e7eb'};
    padding: 0.5rem;
    text-align: left;
    vertical-align: top;
  }
  .note-content table thead {
    background-color: ${isDark ? '#111827' : '#f3f4f6'};
  }
  .note-content table tbody tr:nth-child(odd) {
    background-color: ${isDark ? '#0f172a' : '#fafafa'};
  }
  .note-content table.note-table {
    width: 100%;
    border-collapse: collapse;
    margin: 0.5rem 0;
  }
  .note-content table.note-table th,
  .note-content table.note-table td {
    border: 1px solid ${isDark ? '#374151' : '#e5e7eb'};
    padding: 0.5rem;
    text-align: left;
    vertical-align: top;
  }
  .note-content table.note-table thead {
    background-color: ${isDark ? '#111827' : '#f3f4f6'};
  }
  .note-content table.note-table tbody tr:nth-child(odd) {
    background-color: ${isDark ? '#0f172a' : '#fafafa'};
  }
  .note-content strong {
    font-weight: 600;
  }
  .note-content em {
    font-style: italic;
  }
  .note-content u {
    text-decoration: underline;
  }
  .note-content del {
    text-decoration: line-through;
    color: ${isDark ? '#9ca3af' : '#6b7280'};
  }
  .note-content mark {
    background-color: ${isDark ? '#854d0e' : '#fef08a'};
    color: ${isDark ? '#fef08a' : '#854d0e'};
    padding: 0.1rem 0.2rem;
    border-radius: 0.125rem;
  }
  .note-content blockquote {
    border-left: 3px solid ${isDark ? '#6b7280' : '#9ca3af'};
    padding-left: 1rem;
    margin: 0.25rem 0;
    font-style: italic;
    color: ${isDark ? '#9ca3af' : '#6b7280'};
  }
  .note-content a {
    color: ${isDark ? '#60a5fa' : '#2563eb'};
    text-decoration: underline;
  }
  .note-content h1 {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0.5rem 0;
    line-height: 1.3;
  }
  .note-content h2 {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0.375rem 0;
    line-height: 1.3;
  }
  .note-content h3 {
    font-size: 1.125rem;
    font-weight: 600;
    margin: 0.25rem 0;
    line-height: 1.3;
  }
  .note-content h4 {
    font-size: 1rem;
    font-weight: 600;
    margin: 0.25rem 0;
    line-height: 1.3;
  }
`;

export interface PatientNoteListItemContentCardProps {
  content: string;
  isExpanded: boolean;
  isDark: boolean;
  onHeightChange?: (height: number) => void;
}

export const PatientNoteListItemContentCard: React.FC<PatientNoteListItemContentCardProps> = ({
  content,
  isExpanded,
  isDark,
  onHeightChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const [isInitialized, setIsInitialized] = useState(false);
  const [fullHeight, setFullHeight] = useState<number>(0);

  // Measure full content height whenever the content changes
  useLayoutEffect(() => {
    if (!contentRef.current) return;
    const measuredHeight = contentRef.current.scrollHeight;
    setFullHeight(measuredHeight);
    if (onHeightChange) {
      onHeightChange(measuredHeight);
    }

    const targetHeight = isExpanded
      ? measuredHeight + EXPANDED_BOTTOM_PADDING_PX
      : PREVIEW_MAX_HEIGHT_PX;

    setHeight(targetHeight);
    if (!isInitialized) {
      setIsInitialized(true);
    }
  }, [content, isExpanded, isInitialized, onHeightChange]);

  // Recompute height when toggling expanded/collapsed using the last measured full height
  useLayoutEffect(() => {
    if (fullHeight === 0) return;
    const targetHeight = isExpanded
      ? fullHeight + EXPANDED_BOTTOM_PADDING_PX
      : PREVIEW_MAX_HEIGHT_PX;
    setHeight(targetHeight);
  }, [isExpanded, fullHeight]);

  return (
    <>
      <style>{getNoteContentStyles(isDark)}</style>
      <div
        ref={containerRef}
        className={cn(
          'relative overflow-hidden',
          isInitialized && 'transition-[height] duration-300 ease-in-out'
        )}
        style={{ height: height !== undefined ? `${height}px` : 'auto', minHeight: !isExpanded ? `${PREVIEW_MAX_HEIGHT_PX}px` : undefined }}
      >
        <div
          ref={contentRef}
          className={cn(
            'note-content text-sm leading-relaxed',
            isDark ? 'text-secondary-100' : 'text-secondary-800'
          )}
          dangerouslySetInnerHTML={{ __html: markdownToHtml(content) }}
          aria-live="polite"
          style={{ paddingBottom: isExpanded ? `${EXPANDED_BOTTOM_PADDING_PX}px` : undefined }}
        />
        {!isExpanded && fullHeight > PREVIEW_MAX_HEIGHT_PX && (
          <div
            data-testid="note-gradient-overlay"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-10"
            style={{
              background: isDark
                ? 'linear-gradient(180deg, rgba(2, 11, 37,0) 0%, rgba(2, 11, 37,0.6) 65%, rgba(2, 11, 37,0.9) 100%)'
                : 'linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.4) 70%, rgba(255,255,255,1) 100%)',
              backdropFilter: 'blur(2px)',
              WebkitBackdropFilter: 'blur(2px)',
            }}
          />
        )}
      </div>
    </>
  );
};

